import { BaseService } from '@usercentrics/cmp-browser-sdk';
import { AGB_ANCHOR_NAMES, getAGBAnchor } from '../constants/agb';
import Routes from '../constants/routes';
import { world } from '../languages';
import { AVAILABLE_WORLDS } from '../languages/constants';
import { useCookieConsentStore } from '../stores/cookie-consent/use-cookie-consent-store';
import isMobileAppWebview from '../utils/is-mobile-app-webview';
import isPrerender from '../utils/is-prerender';
import isUITest from '../utils/is-ui-test';
import session from '../utils/session';

export enum USERCENTRICS_SERVICES {
    APPLE_LOGIN = 'Apple Sign-in',
    BRAZE = 'Braze',
    EDUKI = 'eduki.com',
    FACEBOOK = 'Facebook',
    FACEBOOK_SOCIAL = 'Facebook Social Plugins',
    GA = 'Google Analytics',
    GOOGLE = 'Google Sign-In',
    GTM = 'Google Tag Manager',
    MICROSOFT_AZURE = 'Microsoft Azure',
    PINTEREST = 'Pinterest',
    ZENDESK = 'Zendesk'
}

export const INTERNAL_TRACKING_CONSENT_CACHE_KEY = 'uc_internal_tracking_consent';

export const UC_RETRYING_COUNT = 10;
export const UC_RETRYING_DELAY = 1000; // 1s

const usercentrics = {
    addEventListenerOnUcImprintClick() {
        window.addEventListener('onUcImprintClick', () => {
            window.open(`${Routes.IMPRESSUM.path}`, '_blank');
        });
    },

    addEventListenerOnUcPrivacyClick() {
        window.addEventListener('onUcPrivacyClick', () => {
            window.open(`${Routes.AGB.path}${getAGBAnchor(AGB_ANCHOR_NAMES.dataProtection)}`, '_blank');
        });
    },

    addEventListenerOnViewChange() {
        // Update services data after close usercentrics modal for applying changes (accepted cookies)
        window.addEventListener('UC_UI_VIEW_CHANGED', (event) => {
            // @ts-ignore
            if (event.detail.view === 'NONE') {
                this.updateConsentServices();
            }
        });
    },
    getServicesData(): BaseService[] {
        const UC = window.UC_UI;
        let servicesBaseInfo = [];

        if (UC) {
            servicesBaseInfo = UC.getServicesBaseInfo();
        }

        return servicesBaseInfo;
    },
    updateConsentServices() {
        const services = this.getServicesData();

        useCookieConsentStore.getState().setServices(services);
        window.cookiesServicesData = services;
    },
    init() {
        let retryInitializingCounter = 0;
        let isUcInitialized = false;
        const initializationInterval = setInterval(() => {
            if (isUcInitialized || retryInitializingCounter > UC_RETRYING_COUNT) {
                clearInterval(initializationInterval);

                return;
            }

            if (window.UC_UI?.isInitialized()) {
                isUcInitialized = true;
                this.updateConsentServices();
                this.addEventListenerOnViewChange();
                this.addEventListenerOnUcPrivacyClick();
                this.addEventListenerOnUcImprintClick();
            } else {
                retryInitializingCounter++;
            }
        }, UC_RETRYING_DELAY);

        // Try to show modal every time until accept because after AGB page (where modal is hidden) we can go to any page
        const tryToShowInterval = setInterval(() => {
            if (this.isShown || (window.UC_UI && !window.UC_UI.isConsentRequired())) {
                clearInterval(tryToShowInterval);

                return;
            }
            this.showModalIfNecessary();
        }, UC_RETRYING_DELAY);
    },

    isServiceAccepted(services: BaseService[], serviceName: string | string[]): boolean {
        // Skip checking for tests and pre-rendering because in these cases all third party libs are allowed
        if (isUITest() || isPrerender()) {
            return true;
        }

        if (window.UC_UI?.isConsentRequired()) {
            return false;
        }

        if (typeof serviceName === 'string') {
            serviceName = [serviceName];
        }

        return services
            .filter((service) => serviceName.includes(service.name))
            .every((service) => Boolean(service.consent.status));
    },

    isEdukiInternalTrackingEnabled(): boolean {
        if (isUITest() || isPrerender() || session.isLoggedIn()) {
            return true;
        }

        if (window.UC_UI?.isInitialized()) {
            const services = this.getServicesData();
            const isEnabled =
                !window.UC_UI.isConsentRequired() &&
                services
                    .filter((service) => service.name === USERCENTRICS_SERVICES.EDUKI)
                    .every((service) => Boolean(service.consent.status));

            try {
                localStorage.setItem(INTERNAL_TRACKING_CONSENT_CACHE_KEY, String(isEnabled));
            } catch {
                // ignore storage errors
            }

            return isEnabled;
        }

        // UC not yet initialized — fall back to cached value for instant response after reload
        try {
            const cached = localStorage.getItem(INTERNAL_TRACKING_CONSENT_CACHE_KEY);

            if (cached !== null) {
                return cached === 'true';
            }
        } catch {
            // ignore storage errors
        }

        return false;
    },

    isShown: false,

    showModalIfNecessary() {
        if (
            !this.isShown &&
            window.UC_UI?.isConsentRequired() &&
            window.location.pathname.indexOf(Routes.AGB.path) < 0 &&
            window.location.pathname.indexOf(Routes.IMPRESSUM.path) < 0 &&
            !isMobileAppWebview()
        ) {
            const language =
                {
                    [AVAILABLE_WORLDS.CN]: 'zh',
                    [AVAILABLE_WORLDS.CZ]: 'cs',
                    [AVAILABLE_WORLDS.DK]: 'da',
                    [AVAILABLE_WORLDS.GR]: 'el',
                    [AVAILABLE_WORLDS.JP]: 'ja',
                    [AVAILABLE_WORLDS.KR]: 'ko',
                    [AVAILABLE_WORLDS.KZ]: 'kk',
                    [AVAILABLE_WORLDS.MY]: 'ms',
                    [AVAILABLE_WORLDS.RS]: 'sr_latn',
                    [AVAILABLE_WORLDS.SE]: 'sv',
                    [AVAILABLE_WORLDS.UA]: 'uk'
                }[world] || world;

            window.UC_UI.updateLanguage(language);
            window.UC_UI.showFirstLayer();

            this.isShown = !this.isShown;
        }
    }
};

export default usercentrics;
